import { useCallback } from 'react'
import { styled } from '@mui/material'
import { AppliedFilterChip } from './AppliedFilterChip'
import { NewThreadForm } from './NewThreadForm'
import { VersionTimelineView } from './VersionTimelineView'
import { TargetFilteredView } from './TargetFilteredView'
import { useDiscussion } from './hooks/useDiscussion'
import { useTargetDiscussions } from './hooks/useTargetDiscussions'
import {
  useDiscussionActionContext,
  useDiscussionStateContext,
} from '../../context/DiscussionContext'

type DiscussionContentProps = {
  projectUuid: string
}

export const DiscussionContent = ({ projectUuid }: DiscussionContentProps) => {
  const {
    versionPageUuid,
    currentVersionUuid,
    templatePageUuid,
    versionList,
    selectedTargetUuid,
    showNewThreadForm,
  } = useDiscussionStateContext()
  const { closeNewThreadForm } = useDiscussionActionContext()

  const discussion = useDiscussion({
    versionPageUuid,
    currentVersionUuid,
    templatePageUuid,
    versionList,
  })

  const targetData = useTargetDiscussions({
    selectedTargetUuid,
    currentVersionUuid,
    templatePageUuid,
    enabled: !!selectedTargetUuid,
  })

  const handleRefreshAfterNewThread = useCallback(async () => {
    await Promise.all([discussion.refreshCurrent(), targetData.refresh()])
  }, [discussion, targetData])

  if (!versionPageUuid) return null

  const currentVersion = versionList.find(v => v.uuid === currentVersionUuid)
  const resultCount = selectedTargetUuid ? targetData.threads.length : 0

  return (
    <>
      <AppliedFilterChip resultCount={resultCount} />

      <Scrollable>
        {!discussion.isLoadingCurrent &&
          discussion.currentDiscussion &&
          showNewThreadForm && (
            <NewThreadForm
              projectUuid={projectUuid}
              versionPageUuid={versionPageUuid}
              discussionUuid={discussion.currentDiscussion.discussionUuid}
              onClose={closeNewThreadForm}
              onRefresh={handleRefreshAfterNewThread}
            />
          )}

        {selectedTargetUuid ? (
          <TargetFilteredView
            projectUuid={projectUuid}
            versionPageUuid={versionPageUuid}
            currentVersionUuid={currentVersionUuid}
            versionList={versionList}
            targetData={targetData}
          />
        ) : (
          <VersionTimelineView
            projectUuid={projectUuid}
            versionPageUuid={versionPageUuid}
            discussion={discussion}
            currentVersion={currentVersion}
          />
        )}
      </Scrollable>
    </>
  )
}

const Scrollable = styled('div')({
  flex: 1,
  overflowY: 'auto',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
})
